import React, { useEffect, useMemo, useState } from "react";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { app } from "./firebase-config";
import sampleVideoSnapshot from "../sampleVideos.json";
import styles from "./SampleVideosPage.module.css";

type SampleVideo = {
  id: string;
  title: string;
  youtubeId: string;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  position: number | null;
};

const VIDEOS_PER_PAGE = 12;

const FALLBACK_SAMPLE_VIDEOS = sampleVideoSnapshot.videos as SampleVideo[];

const SampleVideosPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [sampleVideos, setSampleVideos] = useState<SampleVideo[]>(
    FALLBACK_SAMPLE_VIDEOS
  );

  useEffect(() => {
    const db = getFirestore(app);
    const sampleVideosRef = doc(db, "publicData", "sampleVideos");
    const unsubscribe = onSnapshot(
      sampleVideosRef,
      (snapshot) => {
        const videos = snapshot.data()?.videos;
        if (!Array.isArray(videos) || videos.length === 0) {
          return;
        }

        const nextVideos = videos
          .map((video): SampleVideo | null => {
            if (
              typeof video !== "object" ||
              video === null ||
              typeof video.youtubeId !== "string"
            ) {
              return null;
            }

            return {
              id:
                typeof video.id === "string" && video.id ?
                  video.id :
                  video.youtubeId,
              title:
                typeof video.title === "string" && video.title ?
                  video.title :
                  "Untitled video",
              youtubeId: video.youtubeId,
              publishedAt:
                typeof video.publishedAt === "string" ?
                  video.publishedAt :
                  null,
              thumbnailUrl:
                typeof video.thumbnailUrl === "string" ?
                  video.thumbnailUrl :
                  null,
              position:
                typeof video.position === "number" ?
                  video.position :
                  null,
            };
          })
          .filter((video): video is SampleVideo => video !== null);

        if (nextVideos.length > 0) {
          setSampleVideos(nextVideos);
          setCurrentPage(1);
        }
      },
      (error) => {
        console.error("Error listening to sample videos:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  const sortedVideos = useMemo(
    () =>
      [...sampleVideos].sort(
        (a, b) => {
          if (a.position !== null && b.position !== null) {
            return a.position - b.position;
          }
          return (
            new Date(b.publishedAt ?? 0).getTime() -
            new Date(a.publishedAt ?? 0).getTime()
          );
        }
      ),
    [sampleVideos]
  );
  const totalPages = Math.max(1, Math.ceil(sortedVideos.length / VIDEOS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pageVideos = sortedVideos.slice(
    (currentPage - 1) * VIDEOS_PER_PAGE,
    currentPage * VIDEOS_PER_PAGE
  );

  const movePage = (page: number) => {
    setCurrentPage(Math.min(totalPages, Math.max(1, page)));
  };

  const formatPublishedDate = (publishedAt: string) => {
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || "en", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(publishedAt));
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>{t("sample_videos_page.hero.title")}</h1>
        <p className={styles.intro}>{t("sample_videos_page.hero.intro")}</p>
      </section>

      <section className={styles.grid} aria-label={t("sample_videos_page.grid_label")}>
        {pageVideos.map((sample) => (
          <article key={sample.id} className={styles.card}>
            <div className={styles.videoBox}>
              <iframe
                className={styles.iframe}
                src={`https://www.youtube.com/embed/${sample.youtubeId}`}
                title={`${sample.title} | AUTOVID`}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className={styles.videoInfo}>
              <h2 className={styles.videoTitle}>{sample.title}</h2>
              <p className={styles.videoDescription}>
                {t("sample_videos_page.hero.intro")}
              </p>
              <div className={styles.videoMeta}>
                {sample.publishedAt ? (
                  <time dateTime={sample.publishedAt}>
                    {formatPublishedDate(sample.publishedAt)}
                  </time>
                ) : null}
                <a
                  className={styles.videoLink}
                  href={`https://www.youtube.com/watch?v=${sample.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("youtube_rankings.page.watch", {
                    defaultValue: "Watch on YouTube",
                  })}
                </a>
              </div>
            </div>
          </article>
        ))}
      </section>

      {totalPages > 1 ? (
        <nav
          className={styles.pagination}
          aria-label={t("sample_videos_page.pagination_label", {
            defaultValue: "Sample videos pages",
          })}
        >
          <button
            type="button"
            className={styles.pageButton}
            disabled={currentPage === 1}
            onClick={() => movePage(currentPage - 1)}
          >
            {t("sample_videos_page.pagination.previous", {
              defaultValue: "Previous",
            })}
          </button>

          {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
            <button
              key={page}
              type="button"
              className={`${styles.pageButton} ${
                page === currentPage ? styles.activePageButton : ""
              }`}
              aria-current={page === currentPage ? "page" : undefined}
              onClick={() => movePage(page)}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            className={styles.pageButton}
            disabled={currentPage === totalPages}
            onClick={() => movePage(currentPage + 1)}
          >
            {t("sample_videos_page.pagination.next", {
              defaultValue: "Next",
            })}
          </button>
        </nav>
      ) : null}
    </div>
  );
};

export default SampleVideosPage;
